// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { replaceInChain } from "@insite/client-framework/HandlerCreator";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    chain,
    LoadRealTimeInventory,
    LoadRealTimePrices,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/LoadProductPricingAndInventory";

// Xng-Change: modifying function to get real time pricing for parent product only in case it has variants.
export const XngLoadRealTimePrices: typeof LoadRealTimePrices = async props => {
    // Xng-Change start
    if (
        !props.parameter.product ||
        !props.parameter.productInfosById ||
        (props.parameter.product.isVariantParent && !props.parameter.styledOption)
    ) {
        return;
    }

    const styledOptionProductId = props.parameter.productInfosById
        ? Object.keys(props.parameter.productInfosById).find(key =>
              props.parameter.productInfosById?.[key]?.productDetailPath?.includes(
                  props.parameter.styledOption as string,
              ),
          )
        : undefined;

    const productIds = (
        styledOptionProductId ? [styledOptionProductId] : Object.keys(props.parameter.productInfosById)
    ) as string[];
    // Xng-Change end
    const productPriceParameters = productIds.map(productId => {
        const productInfo = props.parameter.productInfosById![productId as string];
        return {
            productId: productInfo!.productId,
            unitOfMeasure: productInfo!.unitOfMeasure,
            qtyOrdered: productInfo!.qtyOrdered,
        };
    });

    props.dispatch(
        loadRealTimePricing({
            productPriceParameters,
            onComplete: pricingProps => {
                if (pricingProps.apiResult?.realTimePricingResults) {
                    const pricing = pricingProps.apiResult.realTimePricingResults!.find(o =>
                        styledOptionProductId
                            ? o.productId === styledOptionProductId
                            : o.productId === props.parameter.product?.id,
                    );
                    if (pricing) {
                        props.dispatch({
                            type: "Pages/ProductDetails/CompleteLoadRealTimePricings",
                            pricings: pricingProps.apiResult.realTimePricingResults,
                        });
                    } else {
                        props.dispatch({
                            type: "Pages/ProductDetails/FailedLoadRealTimePricings",
                            productIds,
                        });
                    }
                } else if (pricingProps.error) {
                    props.dispatch({
                        type: "Pages/ProductDetails/FailedLoadRealTimePricings",
                        productIds,
                    });
                }

                props.pricingLoaded = true;
            },
        }),
    );

    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

// Xng-Change: modifying function to get real time inventory for parent product only in case it has variants.
export const XngLoadRealTimeInventory: typeof LoadRealTimeInventory = props => {
    if (props.parameter.product?.isVariantParent && !props.parameter.styledOption) {
        return;
    }
    LoadRealTimeInventory(props);
};

replaceInChain(chain, LoadRealTimePrices, XngLoadRealTimePrices);
replaceInChain(chain, LoadRealTimeInventory, XngLoadRealTimeInventory);
